import { useState } from "react";
import Button from "./Button";
import { usePatchQuery, usePostQuery } from "../hooks";

const FORM_FIELDS = {
  select: SelectField,
  date: DateField,
  email: EmailField,
  text: TextField,
  hidden: HiddenField,
  password: PasswordField,
  number: NumberField,
};

function isAnArrayOfStrings(data) {
  return Array.isArray(data) && data.every((item) => typeof item === "string");
}

function processErrors(data, fields) {
  if (data === null) {
    // If there are no errors, it means we couldn't decode the response json,
    // so assume that's a server error
    return { nonFieldErrors: ["Sorry! An unknown error occurred."] };
  }
  if (typeof data === "object") {
    if (Array.isArray(data)) {
      // If the returned data is an array, wrap it in a nonFieldErrors key
      return {
        nonFieldErrors: data,
      };
    } else {
      const processedErrors = {};
      for (const key in data) {
        if (key === "error") {
          // Non-DRF views that return a simple error message
          processedErrors["nonFieldErrors"] = [data.error];
        } else if (isAnArrayOfStrings(data[key])) {
          // Else it's an object, so likely in the format we want
          // ie. {"email": ["Enter a valid email address."], "nonFieldErrors": ["A non field error"]}
          processedErrors[key] = data[key];
        } else {
          console.error("Unknown data type", data);
        }
      }
      // Check if the keys match any of the fields or nonFieldErrors
      // If they don't, add them to nonFieldErrors
      const outputs = fields
        .filter((field) => field.type !== "hidden")
        .map((field) => field.id);
      outputs.push("nonFieldErrors");
      for (const key in processedErrors) {
        if (!outputs.includes(key)) {
          if (!processedErrors.nonFieldErrors) {
            processedErrors.nonFieldErrors = [];
          }
          processedErrors.nonFieldErrors.push(
            `${key}: ${processedErrors[key].join(", ")}`
          );
        }
      }
      return processedErrors;
    }
  } else {
    // If the response is a string, wrap it in a nonFieldErrors key
    return { nonFieldErrors: [data] };
  }
}

export function Input({
  type,
  id,
  name,
  required,
  value,
  onChange,
  readOnly = false,
  ...rest
}) {
  return (
    <input
      type={type}
      id={id}
      name={name}
      className={`px-3 py-2 border rounded ${readOnly ? "bg-gray-100" : ""}`}
      required={required}
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      {...rest}
    />
  );
}

export function HiddenField({ id, value }) {
  return <input type="hidden" id={id} name={id} value={value} />;
}

function Field({ label, id, errors, children }) {
  return (
    <div className="flex flex-col gap-[0.3rem]">
      {label && <Label htmlFor={id}>{label}</Label>}
      {children}
      <ErrorList errors={errors} />
    </div>
  );
}

export function NumberField({
  name,
  id,
  required,
  errors,
  value,
  readOnly = false,
  onChange,
}) {
  return (
    <Field label={name} id={id} errors={errors}>
      <Input
        type="number"
        id={id}
        name={id}
        required={required}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
      />
    </Field>
  );
}

export function SelectField({
  name,
  id,
  required,
  options,
  errors,
  value,
  readOnly = false,
  multiple = false,
  onChange,
}) {
  const selectValue = multiple ? value || [] : value;

  return (
    <Field label={name} id={id} errors={errors}>
      <select
        id={id}
        name={id}
        className={`w-full px-3 py-2 border rounded appearance-none ${
          readOnly ? "bg-gray-100" : ""
        }`}
        required={required}
        multiple={multiple}
        value={selectValue}
        onChange={(e) => {
          const selectedValues = Array.from(e.target.selectedOptions).map(
            (option) => option.value
          );
          onChange({
            target: {
              name: id,
              value: multiple ? selectedValues : selectedValues[0],
            },
          });
        }}
        disabled={readOnly}
      >
        {options.map((option, index) => (
          <option key={index} value={option.value}>
            {option.name}
          </option>
        ))}
      </select>
    </Field>
  );
}

export function DateField({
  name,
  id,
  required,
  errors,
  value,
  readOnly = false,
  onChange,
}) {
  return (
    <Field label={name} id={id} errors={errors}>
      <Input
        type="date"
        id={id}
        name={id}
        required={required}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
      />
    </Field>
  );
}

export function EmailField({
  name,
  id,
  required,
  errors,
  value,
  readOnly = false,
  onChange,
}) {
  return (
    <Field label={name} id={id} errors={errors}>
      <Input
        type="email"
        id={id}
        name={id}
        required={required}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
      />
    </Field>
  );
}

export function PasswordField({
  name,
  id,
  required,
  errors,
  value,
  readOnly = false,
  onChange,
}) {
  return (
    <Field label={name} id={id} errors={errors}>
      <Input
        type="password"
        id={id}
        name={id}
        required={required}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
      />
    </Field>
  );
}

export function TextField({
  name,
  id,
  required,
  errors,
  value,
  readOnly = false,
  onChange,
}) {
  return (
    <Field label={name} id={id} errors={errors}>
      <Input
        type="text"
        id={id}
        name={id}
        required={required}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
      />
    </Field>
  );
}

function ErrorList({ errors }) {
  if (!errors) {
    return null;
  }
  return (
    <div className="text-red-500 mt-2">
      {errors.map((error, key) => {
        return <p key={key}>{error}</p>;
      })}
    </div>
  );
}

function Label({ htmlFor, children }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block font-medium text-gray-700 text-sm"
    >
      {children}
    </label>
  );
}

function getDefaultForEmptyField(field) {
  // If the field is a select, and has options, use the first option's value
  if (field.type === "select" && field.options && field.options.length > 0) {
    if (field.multiple) {
      return [];
    }
    return field.options[0].value;
  }
  // If the field is some variant of text, use an empty string as the default
  if (
    field.type === "text" ||
    field.type === "email" ||
    field.type === "password" ||
    field.type === "date" ||
    field.type === "number"
  ) {
    return "";
  }
  return undefined;
}

export default function Form({
  path,
  fields,
  submitCallback,
  onChangeCallback,
  id = null,
  method = "POST",
  submitText = "Save",
  initialValues = {},
}) {
  function getValueForField(field) {
    if (values[field.id] !== undefined) {
      return values[field.id];
    }
    // The initial values _might_ have changed after the initial state was set
    // eg. for a dynamic field
    if (initialValues[field.id] !== undefined) {
      return initialValues[field.id];
    }
    return getDefaultForEmptyField(field);
  }

  const [values, setValues] = useState({ ...initialValues });
  const [errors, setErrors] = useState({});

  const postMutation = usePostQuery(path);
  const patchMutation = usePatchQuery(path, { identifier: id });

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (onChangeCallback) {
      onChangeCallback(name, value);
    }
    setValues((prev) => {
      return { ...prev, [name]: value };
    });
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setErrors({});

    try {
      let data;
      let valuesToSubmit = {};
      fields.forEach((field) => {
        valuesToSubmit[field.id] = getValueForField(field);
      });
      if (method === "POST") {
        data = await postMutation.mutateAsync(valuesToSubmit);
      } else {
        data = await patchMutation.mutateAsync(valuesToSubmit);
      }

      if (submitCallback) {
        submitCallback(data);
      }
    } catch (error) {
      if (error.name === "ApiError") {
        setErrors(processErrors(error.data, fields));
      } else {
        setErrors({
          nonFieldErrors: ["An unexpected error occurred. Please try again."],
        });
      }
    }
  }

  const loading = postMutation.isPending || patchMutation.isPending;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {fields.map((field, index) => {
        const fieldErrors = errors?.[field.id];
        const Component = FORM_FIELDS[field.type];
        if (!Component) {
          throw new Error(
            `Unknown field type: ${field.type} for field ${field.id}`
          );
        }
        return (
          <Component
            key={index}
            {...field}
            value={getValueForField(field)}
            onChange={handleChange}
            errors={fieldErrors}
          />
        );
      })}
      {errors.nonFieldErrors &&
        errors.nonFieldErrors.map((error, index) => {
          return (
            <div key={index} className="text-red-500 mt-2">
              {error}
            </div>
          );
        })}
      <Button type="submit" loading={loading} disabled={loading}>
        {submitText}
      </Button>
    </form>
  );
}
